import type { GridApi, NewColumnsLoadedEvent } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useBlotterTableStorage } from './BlotterTableStorageContext';
import { type ColumnDynamics, getCustomColDefProperties } from './columns';
import type { DefaultedUsePersistedBlotterTableOptions } from './usePersistedBlotterTableUtils';
import { safeGridApi } from './utils';

export interface UsePersistedColumnDynamicsParams {
  blotterID: string | null;
  options: Pick<DefaultedUsePersistedBlotterTableOptions, 'persistGridState'>;
}

export interface UsePersistedColumnDynamicsOutput {
  /** Event handlers that need to be registered with AgGrid (done by useBlotterTable) for this hook's persistence model to work */
  eventHandlers: {
    onNewColumnsLoaded: (event: NewColumnsLoadedEvent) => void;
  };
}

export function usePersistedColumnDynamics({
  blotterID,
  options,
}: UsePersistedColumnDynamicsParams): UsePersistedColumnDynamicsOutput {
  const { persistGridState } = options;
  const { getState, setExtraGridState } = useBlotterTableStorage();

  const getPersistedColumnDynamicsState = useCallback(
    (columnDynamics: ColumnDynamics[] | undefined) => {
      if (!blotterID || !persistGridState) {
        return;
      }

      const currentState = getState(blotterID);
      setExtraGridState(blotterID, {
        ...currentState?.extraGridState,
        columnDynamics,
      });
    },
    [blotterID, getState, persistGridState, setExtraGridState]
  );

  const onNewColumnsLoaded = useCallback(
    (event: NewColumnsLoadedEvent) => {
      if (!blotterID || !persistGridState) {
        return;
      }

      const api = safeGridApi(event.api);
      if (!api) {
        return;
      }

      const nextColumnDynamics = getColumnDynamicsFromGridApi(api);
      const currentColumnDynamics = getState(blotterID)?.extraGridState?.columnDynamics;

      if (isEqual(currentColumnDynamics ?? [], nextColumnDynamics ?? [])) {
        return;
      }

      getPersistedColumnDynamicsState(nextColumnDynamics);
    },
    [blotterID, getState, persistGridState, getPersistedColumnDynamicsState]
  );

  return useMemo(
    () => ({
      eventHandlers: {
        onNewColumnsLoaded,
      },
    }),
    [onNewColumnsLoaded]
  );
}

function getColumnDynamicsFromGridApi(api: GridApi): ColumnDynamics[] {
  return (api.getAllGridColumns() ?? []).flatMap(column => {
    const colDef = column.getColDef();
    const customProperties = getCustomColDefProperties(colDef);
    if (customProperties?.dynamics == null) {
      return [];
    }

    return [
      {
        colID: customProperties.dynamics.colID,
        spec: customProperties.dynamics.spec,
        removable: customProperties.dynamics.removable,
        initialState: customProperties.dynamics.initialState,
        state: customProperties.dynamics.state,
      },
    ];
  });
}
